import React from 'react';

export interface ModuleCardProps {
  /** Emoji or icon element shown inside the icon chip */
  icon: React.ReactNode;
  /** Background colour variant for the icon chip */
  iconVariant?: 'green' | 'earth' | 'amber' | 'blue' | 'gray';
  /** Module title */
  title: string;
  /** Short description shown below the title */
  description: string;
  /** Record count shown as a badge. Omit (or pass undefined) for loading state. */
  count?: number | string;
  /** Singular/plural label appended to the count, e.g. "customers" */
  countLabel?: string;
  /** Show or hide the count badge */
  showCount?: boolean;
  /** Click / navigation handler */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
}

const iconVariantClasses: Record<NonNullable<ModuleCardProps['iconVariant']>, string> = {
  green: 'bg-green-100',
  earth: 'bg-earth-light',
  amber: 'bg-amber-50',
  blue: 'bg-blue-50',
  gray: 'bg-gray-100',
};

/**
 * ModuleCard — Dashboard navigation tile.
 *
 * Rendered as a `<button>` so it is keyboard-accessible and carries
 * native click semantics. The icon chip colour is controlled via
 * `iconVariant`; the count badge pulses while `count` is undefined.
 */
export const ModuleCard: React.FC<ModuleCardProps> = ({
  icon,
  iconVariant = 'green',
  title,
  description,
  count,
  countLabel = '',
  showCount = true,
  onClick,
  className = '',
}) => {
  const isLoading = count === undefined;

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'group relative flex w-full items-start gap-4 overflow-hidden rounded',
        'border border-green-200 bg-white p-5 text-start',
        'transition-agrobook duration-agrobook',
        'hover:-translate-y-0.5 hover:border-green-600 hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 focus-visible:ring-offset-2',
        className,
      ].join(' ')}
    >
      {/* Icon chip */}
      <div
        className={[
          'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-2xl',
          iconVariantClasses[iconVariant],
        ].join(' ')}
        aria-hidden="true"
      >
        {icon}
      </div>

      {/* Module info */}
      <div className="flex-1 pe-4">
        <p className="font-display text-base font-semibold text-gray-900">
          {title}
        </p>
        <p className="mt-0.5 font-body text-xs leading-snug text-gray-500">
          {description}
        </p>

        {/* Count badge */}
        {showCount && (
          <div className="mt-2">
            {isLoading ? (
              <span className="inline-block h-5 w-16 animate-pulse rounded-full bg-green-100" />
            ) : (
              <span className="inline-block rounded-full bg-green-100 px-2.5 py-0.5 font-body text-xs font-semibold text-green-800">
                {count} {countLabel}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Trailing chevron */}
      <span
        className="transition-agrobook absolute end-4 top-1/2 -translate-y-1/2 font-body text-xl text-gray-300 duration-agrobook group-hover:text-green-600"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
};
